import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { usePlan } from '../../contexts/PlanContext';
import FeatureGate from '../../components/FeatureGate';
import {
  Database, Download, ChevronLeft, ChevronRight,
  CheckCircle, Clock, Loader2, XCircle
} from 'lucide-react';

interface BackupJob {
  id: string;
  tenant_id: string;
  status: string;
  size_bytes: number;
  started_at: string;
  completed_at: string | null;
  type: string;
}

interface TenantInfo {
  id: string;
  name: string;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL');
}

const PAGE_SIZE = 10;

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
    case 'running': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    case 'pending': return <Clock className="w-4 h-4 text-amber-500" />;
    default: return <Database className="w-4 h-4 text-gray-400" />;
  }
};

const statusLabels: Record<string, string> = {
  completed: 'Voltooid', failed: 'Mislukt', running: 'Bezig', pending: 'Wachtend',
};

const statusColors: Record<string, string> = {
  completed: 'bg-green-50 text-green-700', failed: 'bg-red-50 text-red-700',
  pending: 'bg-amber-50 text-amber-700', running: 'bg-blue-50 text-blue-700',
};

export default function TenantBackups() {
  const [backups, setBackups] = useState<BackupJob[]>([]);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { features, isFeatureEnabled } = usePlan();

  const manualBackupAllowed = isFeatureEnabled('manual_backup');
  const maxCount = features?.backup_max_count ?? 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myTenants] = await Promise.all([
          api.get<TenantInfo[]>('/tenants/mine').catch(() => [] as TenantInfo[]),
        ]);
        setTenants(myTenants);

        // Fetch backups for each of my tenants
        const allBackups: BackupJob[] = [];
        for (const t of myTenants) {
          try {
            const res = await api.get<{ backups: BackupJob[] }>(`/backups/${t.id}`);
            allBackups.push(...(res.backups || []));
          } catch { /* no backups */ }
        }
        setBackups(allBackups);
      } catch { /* not authenticated */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  function getTenantName(tenantId: string): string {
    return tenants.find(t => t.id === tenantId)?.name ?? tenantId;
  }

  const sortedBackups = [...backups].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );
  const totalPages = Math.max(1, Math.ceil(sortedBackups.length / PAGE_SIZE));
  const paginatedBackups = sortedBackups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const manualCount = backups.filter(b => b.type === 'manual').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
        <p className="text-sm text-gray-500 mt-1">Overzicht van backups van je websites</p>
      </div>

      {/* Backup limits info */}
      {manualBackupAllowed && maxCount !== -1 && (
        <div className="text-xs text-gray-500">
          Handmatige backups: {manualCount}/{maxCount}
        </div>
      )}

      <FeatureGate feature="manual_backup">
        <div />
      </FeatureGate>

      <div className="card">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Backup geschiedenis</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : paginatedBackups.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">
            Nog geen backups beschikbaar.
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {paginatedBackups.map(b => (
                <div key={b.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={b.status} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {getTenantName(b.tenant_id)}
                        </span>
                        <span className={`inline-flex px-1.5 py-0.5 text-xs rounded ${statusColors[b.status] ?? 'bg-gray-50 text-gray-500'}`}>
                          {statusLabels[b.status] ?? b.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        <span>{formatDate(b.completed_at ?? b.started_at)}</span>
                        {b.size_bytes > 0 && <span>{formatBytes(b.size_bytes)}</span>}
                      </div>
                    </div>
                  </div>
                  {b.status === 'completed' && (
                    <button
                      onClick={() => window.location.href = `/api/backups/download/${b.id}`}
                      className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                <span className="text-xs text-gray-400">{sortedBackups.length} backup{sortedBackups.length !== 1 ? 's' : ''}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
